import { Component, inject, input, OnInit, signal } from '@angular/core';
import { LoadListener } from '~console/app/~shared/utils/rxjs';
import { Payment, TransactionResultStatus } from '~core/models/payment.models';
import { BaseComponent } from '~console/app/~shared/abstract-components/base.component';
import { catchError, finalize, map, of, switchMap, tap, take } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ROUTES } from '~console/app/~shared/constants/routes.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpResponse } from '@angular/common/http';
import { parse as parseContentDisposition } from 'content-disposition-attachment';
import downloadFile from '~console/app/~shared/utils/download-file';
import { Clipboard } from '@angular/cdk/clipboard';
import { PaymentService } from '~core/services/payment.service';
import { ResourceFacade } from '~console/app/~store/facades/resource.facade';
import { EventService } from '~console/app/~shared/services/event.service';

@Component({
  selector: 'stickers-payment-status',
  templateUrl: './stickers-payment-status.component.html',
  styleUrls: ['./stickers-payment-status.component.scss'],
  standalone: false
})
export class StickersPaymentStatusComponent
  extends BaseComponent
  implements OnInit
{
  private readonly _paymentService = inject(PaymentService);
  private readonly _router = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _clipboard = inject(Clipboard);
  private readonly _resourceFacade = inject(ResourceFacade);
  private readonly _eventService = inject(EventService);

  paymentId = input<string | null | undefined>();
  transactionStatus = input<TransactionResultStatus | null | undefined>();

  paymentListener: LoadListener<Payment | null> | undefined;
  isDownloadingInvoice = signal(false);

  ngOnInit(): void {
    this.loadPayment();
  }

  loadPayment(): void {
    const paymentId$ = this.paymentId()
      ? of(this.paymentId())
      : this._activatedRoute.queryParams.pipe(
          takeUntilDestroyed(this._destroyRef),
          take(1),
          map(params => params['paymentId'] as string | undefined)
        );

    const payment$ = paymentId$.pipe(
      switchMap(paymentId => {
        if (!paymentId) {
          this._router.navigate([ROUTES.home]);
          return of(null);
        }
        return this._paymentService.read(paymentId).pipe(
          tap({
            error: error => {
              this._loggerService.debug('Error in load payment:', error);
            }
          }),
          catchError(() => {
            this._router.navigate([ROUTES.home]);
            return of(null);
          })
        );
      })
    );

    this.paymentListener = new LoadListener(payment$);
  }

  redirectRoute(): void {
    this._resourceFacade.getData({ refresh: true });
    this._eventService.emit({ type: 'REFRESH_STICKERS_METRICS' });
    this._router.navigate([ROUTES.stickers]);
    this._dialogService.closeAll();
  }

  downloadInvoice(paymentId: string): void {
    this.isDownloadingInvoice.set(true);
    this._paymentService
      .downloadInvoice(paymentId)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: value => {
            const _value = value as HttpResponse<unknown>;
            const contentType = _value.headers.get('content-type');
            const disposition = _value.headers.get('content-disposition');
            const filename = (
              parseContentDisposition(disposition || '') as {
                filename: string;
              }
            ).filename;
            try {
              if (!contentType) throw contentType;
              setTimeout(() => {
                downloadFile({
                  data: _value.body,
                  filename: filename
                });
              }, 200);
            } catch (error) {
              console.error('content-type is missing', error);
            }
          },
          error: () => {
            this.isDownloadingInvoice.set(false);
          },
          complete: () => {
            this.isDownloadingInvoice.set(false);
          }
        }),
        finalize(() => {
          this.isDownloadingInvoice.set(false);
        })
      )
      .subscribe();
  }

  copyTransactionId(transactionId: string): void {
    const success = this._clipboard.copy(transactionId);
    if (success) {
      this._toastService.open({
        content: 'Référence de paiement copiée dans le presse-papiers',
        type: 'success'
      });
    } else {
      this._toastService.open({
        content: 'Erreur lors de la copie',
        type: 'error'
      });
    }
  }
}
